import { Injectable, signal } from '@angular/core';

/** Keep in sync with the toast exit transition in `app-toast.component.css`. */
export const TOAST_EXIT_ANIMATION_MS = 220;

export type ToastKind = 'neutral' | 'success' | 'error' | 'info';

export type ToastState = Readonly<{
  message: string;
  kind: ToastKind;
}>;

export type ToastOptions = Readonly<{
  kind?: ToastKind;
  durationMs?: number;
}>;

const DEFAULT_DURATION: Record<ToastKind, number> = {
  neutral: 3500,
  success: 3200,
  error: 4800,
  info: 3800,
};

@Injectable({ providedIn: 'root' })
export class ToastService {
  readonly active = signal<ToastState | null>(null);

  /** True while the toast plays its exit animation, just before it is removed. */
  readonly leaving = signal(false);

  private hideTimer?: ReturnType<typeof setTimeout>;
  private removeTimer?: ReturnType<typeof setTimeout>;

  /** @param durationOrOptions Legacy: number = duration in ms. */
  show(message: string, durationOrOptions?: number | ToastOptions): void {
    let kind: ToastKind = 'neutral';
    let durationMs: number | undefined;

    if (typeof durationOrOptions === 'number') {
      durationMs = durationOrOptions;
    } else if (durationOrOptions) {
      kind = durationOrOptions.kind ?? 'neutral';
      durationMs = durationOrOptions.durationMs;
    }

    this.present({ message: message.trim(), kind }, durationMs ?? DEFAULT_DURATION[kind]);
  }

  showSuccess(message: string, durationMs?: number): void {
    this.present({ message: message.trim(), kind: 'success' }, durationMs ?? DEFAULT_DURATION.success);
  }

  showError(message: string, durationMs?: number): void {
    this.present({ message: message.trim(), kind: 'error' }, durationMs ?? DEFAULT_DURATION.error);
  }

  showInfo(message: string, durationMs?: number): void {
    this.present({ message: message.trim(), kind: 'info' }, durationMs ?? DEFAULT_DURATION.info);
  }

  clear(): void {
    this.clearTimers();
    this.active.set(null);
    this.leaving.set(false);
  }

  dismiss(): void {
    if (!this.active()) return;
    this.beginHide();
  }

  private present(state: ToastState, durationMs: number): void {
    if (!state.message) return;
    this.clearTimers();
    this.active.set(state);
    this.leaving.set(false);
    this.hideTimer = window.setTimeout(() => this.beginHide(), durationMs);
  }

  private beginHide(): void {
    if (!this.active()) return;
    this.leaving.set(true);
    this.removeTimer = window.setTimeout(() => {
      this.active.set(null);
      this.leaving.set(false);
    }, TOAST_EXIT_ANIMATION_MS);
  }

  private clearTimers(): void {
    clearTimeout(this.hideTimer);
    clearTimeout(this.removeTimer);
    this.hideTimer = undefined;
    this.removeTimer = undefined;
  }
}
